import * as React from "react";
import { Timeline, TimelineEvent } from "react-event-timeline";
import { LocalizedDateTime, DateTimeAccuracy } from "../../misc/components/LocalizedDateTime";
import {
    Query,
    QueryCondition,
    QueryOrderDirection,
    AuditTrailEntity,
    CoughAnalysisSet as OldApiCoughAnalysisSet,
} from "../../misc/api/apiTypes";
import { getQueryFilters } from "../../misc/components/queryTable/ReducerUtilities";
import { ItemMetadata } from "../../misc/components/analysis/ItemMetadata";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { UserFilter } from "./UserFilter";
import { ActionFilter } from "./ActionFilter";
import Modal, { ModalSize } from "../../misc/components/modal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { AnalysisSetReferenceTag } from "../../cough-analysis/Shared/AnalysisSetReferenceTag";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { QueryFilterIcon } from "../../misc/components/QueryFilterIcon";
import * as _ from 'lodash';
import HistoryViewCommentsDetails from "./HistoryViewReviewComments";
import HistoryReviewSummary from "./HistoryReviewSummary";
import { WebPortalClient, CoughAnalysisSet, CoughSession } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { faDesktop, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface HistoryViewProps {
    analysisSet: CoughAnalysisSet;
    onClose: () => void;
}

declare const rootUrl: string;

export const HistoryView = (props: HistoryViewProps) => {
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [hasError, setError] = React.useState(false);
    const [oldApiAnalysisSet, setOldApiAnalysisSet] = React.useState<OldApiCoughAnalysisSet>();
    const [session, setSession] = React.useState<CoughSession>();
    const [actionTypeFilterVisible, setActionTypeFilterVisible] = React.useState(false);
    const [userFilterVisible, setUserFilterVisible] = React.useState(false);
    const { study } = useCurrentStudyConfigurationContext();

    const [auditQuery, setAuditQuery] = React.useState<Query<any>>({
        Data: [],
        Filters: [{ Member: "TypeName", Values: ["CoughAnalysisSet", "AnalysisSet", "AnalysisSetFixedComment"] }],
        Order: { Member: "Event.EventDateTime", Direction: QueryOrderDirection.Descending }
    })

    React.useEffect(() => {
        const fetchAnalysisSet = async (as: OldApiCoughAnalysisSet) => {
            if (props.analysisSet) {
                const query: Query<AuditTrailEntity> = {
                    ...auditQuery,
                    Filters: [
                        ...auditQuery.Filters,
                        { Member: "RecordId", Values: [as.OldId, "," + as.OldId + "]", "[" + as.OldId + ","] }
                    ]
                };
                await fetchEvents(query);
            } else {
                setError(true);
            }
        }

        const getOldApiAnalysisSet = async () => {
            // HACK: We need the DB Id for querying the audit trail entities currently (prior to that endpoint being refactored) and the v3 api does not expose this information
            const as = await legacyClient.getCoughAnalysisSet(props.analysisSet.id);
            setOldApiAnalysisSet(as);

            const sessionResult = await client.client.getCoughSession(props.analysisSet.sessionId);
            setSession(sessionResult);

            fetchAnalysisSet(as);
        }

        getOldApiAnalysisSet();
    }, [props.analysisSet.id]);



    const fetchEvents = async (eventsQuery: Query<AuditTrailEntity>) => {
        const eventsQueryResult = await legacyClient.getAnalysisSetAuditEntities(eventsQuery);

        setAuditQuery({
            ...eventsQueryResult,
            //The history view should only be displaying one event even if more
            //than one entity is updated in the same event.
            Data: _.uniqBy(eventsQueryResult.Data, data => data.Event.Id)
        });
    }

    const onActionChange = async e => {
        const filters = getQueryFilters(
            auditQuery,
            "Event.ActionName",
            QueryCondition.Equal,
            e
        );

        const eventQuery: Query<AuditTrailEntity> = {
            ...auditQuery,
            Filters: filters
        };

        await fetchEvents(eventQuery);
    };

    const renderEventTitle = entity => (
        <>
            <span>{entity.Event.ActionName}</span> &nbsp;
            {entity.Event.User ? (
                <>
                    {"("}
                    <FontAwesomeIcon icon={faUser} /> {entity.Event.User.Username}
                </>
            ) : (
                <>
                    {"("}
                    <FontAwesomeIcon icon={faDesktop} /> {"System"}
                </>
            )})
        </>
    );

    const renderBody = () => {
        if (!props.analysisSet || !session) {
            return (
                <div className="modal-body">
                    <LoadingWithMessage message="Loading Analysis Set History..." />
                </div>
            );
        }

        const isUserFilterFiltered =
            auditQuery.Filters.filter(x => x.Member === "Event.User.Username").length >
            0;
        const isActionFilterFiltered =
            auditQuery.Filters.filter(x => x.Member === "Event.ActionName").length > 0;

        return (
            <div className="modal-body">
                <div className="row">
                    <div className="col-md-12">
                        <Alert show={hasError} category={AlertCategoryEnum.Danger}>
                            The selected analysis set doesn't exist
                        </Alert>
                        <ItemMetadata
                            metadata={{
                                study: study.name,
                                siteNumber: props.analysisSet.session.visit.subject.site.number,
                                subjectNumber: props.analysisSet.session.visit.subject.number,
                                technician: session.technician, // HACK: the analysis set doesn't have access to the specific cough fields in session
                                visitName: props.analysisSet.session.visit.name,
                                sessionId: props.analysisSet.session.id,
                                sessionName: props.analysisSet.session.name,
                                rawDuration: session.rawDuration,
                                processedDuration: session.processedDuration,
                                sensorProcessedDuration: session.sensorProcessedDuration,
                            }}
                        />
                    </div>
                    <div className="col-md-6">
                        <button
                            className="btn btn-primary btn-outline"
                            onClick={() => setActionTypeFilterVisible(true)}
                        >
                            Action filter
                            <QueryFilterIcon
                                member="Event.Username.User"
                                filterable={true}
                                filtered={isActionFilterFiltered}
                                onFilter={() => { }}
                            />
                        </button>
                        &nbsp;
                        <button
                            className="btn btn-primary btn-outline"
                            onClick={() => setUserFilterVisible(true)}
                        >
                            User filter
                            <QueryFilterIcon
                                member="Event.Username.User"
                                filterable={true}
                                filtered={isUserFilterFiltered}
                                onFilter={() => { }}
                            />
                        </button>
                        <ActionFilter
                            analysisSetOldId={oldApiAnalysisSet.OldId}
                            isActionFilterVisible={actionTypeFilterVisible}
                            auditQuery={auditQuery}
                            onChange={query => fetchEvents(query)}
                            onClose={() => setActionTypeFilterVisible(false)}
                            filterableEntityTypes={["CoughAnalysisSet", "AnalysisSet", "AnalysisSetFixedComment"]}
                        />
                        <UserFilter
                            analysisSetOldId={oldApiAnalysisSet.OldId}
                            isUserFilterVisible={userFilterVisible}
                            auditQuery={auditQuery}
                            onChange={query => fetchEvents(query)}
                            onClose={() => setUserFilterVisible(false)}
                            filterableEntityTypes={["CoughAnalysisSet", "AnalysisSet", "AnalysisSetFixedComment"]}
                        />
                    </div>
                </div>
                <div className="row" style={{ marginTop: "40px" }}>
                    <div className="col-md-12">
                        <Timeline style={{ fontSize: 100 + "%" }}>
                            {auditQuery.Data.map(x => (
                                <TimelineEvent
                                    key={`TimelineEvent${x.Event.EventDateTime}`}
                                    title={renderEventTitle(x)}
                                    collapsible
                                    showContent
                                    container="card"
                                    createdAt={
                                        <LocalizedDateTime
                                            time={x.Event.EventDateTime}
                                            accuracy={DateTimeAccuracy.Second}
                                        />
                                    }
                                    icon={
                                        x.Event.User ? (
                                            <i
                                                className="fas fa-user"
                                                onClick={() =>
                                                    window.location.replace(
                                                        `${rootUrl}spa/Audit/Event/${x.Event.Id}`
                                                    )
                                                }
                                            />
                                        ) : (
                                            <FontAwesomeIcon icon={faDesktop}
                                                onClick={() =>
                                                    window.location.replace(
                                                        `${rootUrl}spa/Audit/Event/${x.Event.Id}`
                                                    )
                                                }
                                            />
                                        )
                                    }
                                >
                                    {x.Event.SummaryText}
                                    {x.Event.UserAnnotation ? (
                                        <>
                                            <p className="text-muted">
                                                <span className="label label-primary">
                                                    User annotation
                                                </span>{" "}
                                                {x.Event.UserAnnotation}
                                            </p>
                                        </>
                                    ) : null}
                                    <HistoryViewCommentsDetails event={x.Event} />
                                    <HistoryReviewSummary event={x.Event} />
                                </TimelineEvent>
                            ))}
                        </Timeline>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Modal
            show={true}
            size={ModalSize.Large}
            header={
                <>
                    {props.analysisSet && session ? (
                        <>
                            History of{" "}
                            <AnalysisSetReferenceTag typeName={props.analysisSet.typeName} coughSetTypeName="" created={props.analysisSet.created} status={props.analysisSet.statusName} />
                        </>
                    ) : (
                        "Loading..."
                    )}
                </>
            }
            closeModalHandler={props.onClose}
        >
            <ErrorBoundary>{renderBody()}</ErrorBoundary>
        </Modal>
    );
}
